<template>
    <span class="role-selector">
        <Select
            :placeholder="isFetching ? '...' : elementName"
            :loading="isFetching"
            :options="roles"
            :invalid="allLoadedButNoRole"
            :disabled="!isEditing"
            v-model="selectedRole"
            optionLabel="name"
            value="identity"
            @change="changeRole"
            variant="outlined"
            dropdownIcon="-"
            v-tooltip.top="
                'These roles can be selected from what you have defined at the domain level.'
            "
        >
            <template #footer>
                <div class="p-3" @click="handleAddRole">
                    <Button
                        label="Add role"
                        fluid
                        severity="secondary"
                        variant="text"
                        size="small"
                        icon="pi pi-plus"
                    />
                </div>
            </template>
        </Select>
        <i
            v-if="hasConflict"
            class="pi pi-exclamation-triangle"
            style="color: orange"
            v-tooltip="'This role is already assigned to another lane.'"
        />
    </span>
</template>

<script setup lang="ts">
import { type Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import type { SelectChangeEvent } from "primevue/select";
import { computed, ref, watch } from "vue";

import { useBpmnManager } from "@/composables/bpmn-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useProcessManager } from "@/composables/process-manager";
import { Role } from "@/composables/role-manager";
import { useLanesManager } from "@/stores/lanes-store";
import { useRolesManager } from "@/stores/roles-store";

const props = defineProps<{
    element: BPMNElement;
    bpmnManager: ReturnType<typeof useBpmnManager>;
}>();

const { isEditing } = useEditManager();
const { lanes, isFetching: isFetchingLanes } = useLanesManager();
const { roles, isLoading: isLoadingRoles } = useRolesManager();
const { isFetching: isFetchingProcess } = useProcessManager();
const { open: openRoleDialog } = useEditRoleDialog();

const hasConflict = ref(false);

const selectedRole = ref<Role>();
const elementName = computed(
    () => props.element.name || props.element.businessObject?.name || "..."
);
const elementRoleId = ref();

watch(
    [() => props.element, props.bpmnManager.xml],
    () => {
        elementRoleId.value = getBusinessObject(props.element).roleId;
    },
    { immediate: true }
);

const lane = computed(() => {
    return lanes.value?.find((l) => {
        return l.bpmn_id === getBusinessObject(props.element).id;
    });
});

const isFetching = computed(() =>
    Boolean(
        isFetchingLanes.value || isFetchingProcess.value || isLoadingRoles.value
    )
);

watch([selectedRole, props.bpmnManager.xml, isFetching], async () => {
    if (isFetching.value) {
        return;
    }
    const bpmnLanes = (await props.bpmnManager.getLanes()).map(
        getBusinessObject
    );
    hasConflict.value = bpmnLanes.some(
        (l) =>
            l.id !== getBusinessObject(props.element).id &&
            l.roleId === selectedRole.value?.identity
    );
});

// Only returns true when lanes and roles are loaded, but no selectedRole is found.
const allLoadedButNoRole = computed(() => {
    if (isFetching.value) return false;
    if (
        lane.value &&
        roles.value &&
        !selectedRole.value &&
        !elementRoleId.value
    ) {
        return true;
    }
    return false;
});

watch(
    [lane, () => roles.value, elementRoleId],
    ([newLane, currentRoles, roleId]) => {
        if (!currentRoles) return;

        // Match by roleId (from BPMN element) or role_id (from backend lane).
        // Fall back to matching by name when neither ID is available.
        const role =
            currentRoles.find(
                (role) =>
                    role.identity === roleId ||
                    role.identity === newLane?.role_id
            ) ??
            currentRoles.find(
                (role) => role.name === getBusinessObject(props.element).name
            );

        if (role) {
            selectedRole.value = role;
            // Persist the roleId on the BPMN element if it was matched by
            // name so future lookups can use the faster ID path.
            if (!roleId || roleId !== role.identity) {
                props.bpmnManager.updateElementProperties(props.element.id, {
                    name: role.name,
                    roleId: role.identity,
                });
            }
        }
    },
    { immediate: true }
);

const assignRoleToLane = (role: Role) => {
    selectedRole.value = role;
    props.bpmnManager.updateElementProperties(props.element.id, {
        name: role.name,
        roleId: role.identity,
    });
    // Make sure the change is picked up.
    props.bpmnManager.eventBus?.fire("commandStack.changed");
};

const handleAddRole = async () => {
    const createdRole = await openRoleDialog();
    if (!createdRole) {
        return;
    }

    assignRoleToLane(createdRole);
};

const changeRole = (event: SelectChangeEvent) => {
    const roleId = event.value.identity;
    if (!roleId) {
        return;
    }

    const role = roles.value?.find((item) => item.identity === roleId);
    if (!role) {
        return;
    }

    assignRoleToLane(role);
};
</script>

<style scoped lang="scss">
.role-selector {
    display: flex;
    align-items: center;
    position: absolute;
    top: 1px;
    left: 1px;

    :deep(.p-select) {
        background: none;
        border: none;
        box-shadow: none;

        .p-select-dropdown {
            width: 0;
        }

        &.p-invalid {
            .p-select-label,
            .p-select-dropdown {
                color: var(--p-select-invalid-border-color);
            }
        }
    }
}
</style>
